// src/screens/StreamChatPopoutScreen.js
// ============================================================================
// CHAT-ONLY POPOUT (founder 2026-07-23) — /chat/:channel renders JUST the
// stream chat: no player, no HLS, no rails. OWNER-GATED (founder, same day):
// this is the streamer's bandwidth tool (OBS Custom Browser Dock — Docks →
// Custom Browser Docks → this URL — or a popped-out window on a teleprompter);
// everyone else is routed to /s/<channel> so viewers stay on the metered
// watch surface. Admins exempt (standing platform pattern).
//
// Twitch-popout posture: same session id from getStreamPlayback (grace-
// windowed epoch + 10-min offline linger), same StreamChat component as the
// watch page — pin/moderation affordances included for the streamer. Rumble/
// Super Crit lanes are hidden here (their modals live on the watch page).
// NO roster metering: a chat-only surface must never accrue watch-time comp
// signal.
//
// Channel memberships (evergreen 3.5a): the owner's "Members-only" chip and
// its switch work here too (same helper and copy as the watch page), so a
// streamer can run members-only chat from the OBS dock. No Join and no badge
// claims: the popout is the owner's tool. An admin who is not the owner sees
// the gate state read-only (and the plain "members-only" line while it is on:
// the rules give admins no seat in a members-only room).
// ============================================================================

import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, Platform } from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../../firebaseConfig';
import { StreamChat } from './StreamWatchScreen';
import useMembershipsLive from '../hooks/useMembershipsLive';
import { setChannelChatMembersOnly } from '../utils/membershipPayment';

const COLORS = { BG: '#0b0b0b', GOLD: '#FCD000', DIM: '#888', LIVE: '#e02222' };

export default function StreamChatPopoutScreen({ navigation, route }) {
  const channelParam = route?.params?.channel ?? route?.params?.userId;
  const isFocused = useIsFocused();
  const functions = getFunctions();

  const [stream, setStream] = useState(null);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myName, setMyName] = useState(null);
  // Owner gate (founder 2026-07-23): the popout is the STREAMER'S bandwidth
  // tool — everyone else is routed to the watch page (also keeps viewers on
  // the metered surface). undefined = auth not settled yet; set ONCE with the
  // role included so the gate never decides on a half-loaded viewer.
  const [viewer, setViewer] = useState(undefined); // { uid: string|null, isAdmin: bool }
  const [gateChecked, setGateChecked] = useState(false);
  // Static copy only: the turn-on confirm's "can't join right now" line
  // while memberships are dark (a pilot).
  const membershipsFlag = useMembershipsLive();
  const membershipsFlagLive = membershipsFlag?.live === true;
  const toggleMembersOnlyChat = useCallback((on) => setChannelChatMembersOnly({ on: on === true }), []);

  const fetchStream = useCallback(async () => {
    if (!channelParam) return;
    try {
      const fn = httpsCallable(functions, 'getStreamPlayback');
      const result = await fn({ channel: channelParam });
      setStream(result.data || null);
      if (result.data?.userId) setUserId(result.data.userId);
      if (Platform.OS === 'web' && typeof document !== 'undefined' && result.data?.displayName) {
        document.title = `${result.data.displayName} — Chat`;
      }
    } catch (err) {
      console.warn('[ChatPopout] fetch failed:', err.message);
      setStream(null);
    } finally {
      setLoading(false);
    }
  }, [functions, channelParam]);

  useEffect(() => {
    if (!isFocused) return;
    fetchStream();
    const poll = setInterval(fetchStream, 45000);
    return () => clearInterval(poll);
  }, [isFocused, fetchStream]);

  // Wait for Firebase auth to settle (a fresh OBS dock restores the session
  // asynchronously — auth.currentUser is null for a beat even when logged in),
  // then load the viewer's role for the standing admins-exempt pattern.
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) { setViewer({ uid: null, isAdmin: false }); return; }
      let isAdmin = false;
      try {
        const snap = await getDoc(doc(db, 'users', u.uid));
        isAdmin = snap.exists() && snap.data().role === 'admin';
      } catch (_) { /* treated as non-admin */ }
      setViewer({ uid: u.uid, isAdmin });
    });
    return () => unsub();
  }, []);

  // The gate: once the channel is resolved AND auth settled, non-owners
  // (except admins) are routed to the watch page. replace() so back doesn't
  // bounce them into the gate again.
  useEffect(() => {
    if (gateChecked || !userId || viewer === undefined) return;
    if (viewer.uid === userId || viewer.isAdmin) { setGateChecked(true); return; }
    navigation.replace('StreamWatch', { channel: channelParam });
  }, [gateChecked, userId, viewer, navigation, channelParam]);

  // Own public handle for chat attribution — same sourcing as the watch page
  // (public_profiles canonical, users.userName fallback; never displayName).
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    getDoc(doc(db, 'public_profiles', uid)).then((snap) => {
      if (snap.exists() && snap.data().userName) setMyName(snap.data().userName);
    }).catch(() => {});
    const unsub = onSnapshot(doc(db, 'users', uid), (snap) => {
      const d = snap.data() || {};
      setMyName((prev) => prev || d.userName || null);
    }, () => {});
    return () => unsub();
  }, []);

  const chatSessionId = stream ? (stream.chatSessionId || null) : null;

  // Members-only chip (3.5a), owner only; StreamChat also shows it while the
  // gate doc is on (read-only for an admin who is not the owner).
  const isOwner = !!viewer && !!userId && viewer.uid === userId;
  const canToggleMembersOnly = isOwner && (
    stream?.membershipSelf?.membersChatAvailable === true || stream?.membershipSelf?.paused === true
  );
  const membershipsLive = typeof stream?.membershipsLive === 'boolean' ? stream.membershipsLive : null;
  const newJoinsClosed = stream?.membershipSelf?.paused === true || !membershipsFlagLive;

  return (
    <View style={styles.container}>
      {loading || (stream && !gateChecked) ? (
        // Gate still deciding — never flash the chat at someone about to be
        // routed to the watch page.
        <ActivityIndicator color={COLORS.GOLD} style={{ marginTop: 40 }} />
      ) : !stream ? (
        <Text style={styles.notFound}>Streamer not found</Text>
      ) : (
        <StreamChat
          sessionId={chatSessionId}
          senderName={myName}
          streamerUid={userId}
          navigation={navigation}
          style={styles.chatFull}
          hideActions
          title={`${stream.displayName || 'Stream'} — Chat${stream.live ? '  🔴' : ''}`}
          gateKey={userId}
          membershipsLive={membershipsLive}
          canToggleMembersOnly={canToggleMembersOnly}
          onToggleMembersOnly={isOwner ? toggleMembersOnlyChat : null}
          newJoinsClosed={newJoinsClosed}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: COLORS.BG },
  chatFull: { flex: 1, borderWidth: 0, borderRadius: 0 },
  notFound: { color: COLORS.DIM, fontSize: 14, textAlign: 'center', marginTop: 40 },
});
